<script lang="ts">
  import { onDestroy, onMount } from "svelte";
  import "ol/ol.css";
  import Map from "ol/Map";
  import View from "ol/View";
  import TileLayer from "ol/layer/Tile";
  import OSM from "ol/source/OSM";
  import Feature from "ol/Feature";
  import Point from "ol/geom/Point";
  import LineString from "ol/geom/LineString";
  import VectorSource from "ol/source/Vector";
  import VectorLayer from "ol/layer/Vector";
  import { Fill, Icon, Stroke, Style, Text } from "ol/style";
  import { fromLonLat } from "ol/proj";
  import { mode } from "mode-watcher";
  import {
    projectDraft,
    projectGeoCodesChanged,
    projectOriginal,
    roadStore,
  } from "$src/application/projects/stores/projectStore";
  import { useComputeRoadFromPoints } from "$src/presentation/core/hooks/useComputeRoadFromPoints";
  import { type RoadFromPoints } from "$src/application/map/commands/ComputeRoadFromPoints";
  import { type Road } from "$src/domain/map/Road";
  import { isEditActive } from "$src/application/main/mainTemplateStore";

  let map: Map;
  let tileLayer: TileLayer;
  let vectorSource = new VectorSource(); // Utilisation d'un vecteur source commun pour les points et les lignes
  let roadLayer: VectorLayer; // Layer pour la route

  export let fromLatitude: number = $projectOriginal?.info?.store?.location?.latitude ?? 0;
  export let fromLongitude: number = $projectOriginal?.info?.store?.location?.longitude ?? 0;
  export let toLatitude: number = $projectOriginal?.info?.store?.location?.latitude ?? 0;
  export let toLongitude: number = $projectOriginal?.info?.store?.location?.longitude ?? 0;

  $: middleLatitude = (fromLatitude + toLatitude) / 2.0;
  $: middleLongitude = (fromLongitude + toLongitude) / 2.0;

  const computeRoadCommand = useComputeRoadFromPoints();

  $: {
    if (!$isEditActive && $projectOriginal) {
      console.log("compute from original");

      if ($projectOriginal.delivery.isCustomerAddress) {
        const points: RoadFromPoints = {
          from: $projectOriginal.delivery.geoLocationFrom,
          to: $projectOriginal.customer.billingAddressLocation,
        };

        projectGeoCodesChanged.set(points);
      } else {
        const points: RoadFromPoints = {
          from: $projectOriginal.delivery.geoLocationFrom,
          to: $projectOriginal.delivery.geoLocationTo,
        };

        projectGeoCodesChanged.set(points);
      }
    }
  }

  const unsubscribe = projectGeoCodesChanged.subscribe(async (value) => {
    if (value) await computeRoad(value);
  });

  onDestroy(() => {
    unsubscribe();
  });

  async function computeRoad(roadFromPoints: RoadFromPoints) {
      // Réinitialiser les éléments sur la carte
      resetMapElements();

    if (roadFromPoints.from && roadFromPoints.to) {
      const road = await $computeRoadCommand.mutateAsync({
        from: roadFromPoints.from,
        to: roadFromPoints.to,
      });

      if ($isEditActive && $projectDraft) {
        $projectDraft.delivery.carKilometerDistance = road.distanceKilometers;
      }

      roadStore.set(road);

      if (road) {
        // Mettre à jour les coordonnées
        fromLatitude = road.from.latitude;
        fromLongitude = road.from.longitude;
        toLatitude = road.to.latitude;
        toLongitude = road.to.longitude;

        // Ajouter les nouveaux points et la route
        addPointsToMap(fromLatitude, fromLongitude, toLatitude, toLongitude);
        addRoadToMap(road);

        // Recentrer la vue
        fitMapToExtent(fromLongitude, fromLatitude, toLongitude, toLatitude);
      }
    }
  }

  function resetMapElements() {
    // Réinitialiser la source du vecteur pour supprimer toutes les anciennes features
    vectorSource.clear();
  }

  function addPointsToMap(
    fromLat: number,
    fromLon: number,
    toLat: number,
    toLon: number
  ) {
    const pointFeatureFrom = new Feature({
      geometry: new Point(fromLonLat([fromLon, fromLat])),
    });

    const pointFeatureTo = new Feature({
      geometry: new Point(fromLonLat([toLon, toLat])),
    });

    const pointStyleFrom = new Style({
      image: new Icon({
        src: "icons/map-pin.png",
      }),
      text: new Text({
        text: "Magasin",
        offsetY: -20,
        font: "14px Calibri,sans-serif",
        fill: new Fill({ color: "#000" }),
        backgroundFill: new Fill({
          color: "#fff",
        }),
      }),
    });

    const pointStyleTo = new Style({
      image: new Icon({
        src: "icons/map-pin.png",
      }),
      text: new Text({
        text: "Livraison",
        offsetY: -20,
        font: "14px Calibri,sans-serif",
        fill: new Fill({ color: "#000" }),
        backgroundFill: new Fill({
          color: "#fff",
        }),
      }),
    });

    pointFeatureFrom.setStyle(pointStyleFrom);
    pointFeatureTo.setStyle(pointStyleTo);

    // Ajouter les points à la source vectorielle
    vectorSource.addFeatures([pointFeatureFrom, pointFeatureTo]);
  }

  function addRoadToMap(road: Road) {
    // Convertir les coordonnées de roads à partir de GeoLocation en format OpenLayers
    const lineCoordinates = road.roads.map((location) =>
      fromLonLat([location.longitude, location.latitude])
    );

    // Créer une LineString pour représenter la route
    const lineFeature = new Feature({
      geometry: new LineString(lineCoordinates),
    });

    // Appliquer un style à la ligne
    lineFeature.setStyle(
      new Style({
        stroke: new Stroke({
          color: "#ff0000", // Couleur rouge pour la route
          width: 4, // Largeur de la ligne
        }),
      })
    );

    // Ajouter la ligne à la source vectorielle
    vectorSource.addFeature(lineFeature);
  }

  function fitMapToExtent(
    fromLon: number,
    fromLat: number,
    toLon: number,
    toLat: number
  ) {
    // Définir l'extent en utilisant les coordonnées minimales et maximales

    const min = fromLonLat([
      Math.min(fromLon, toLon),
      Math.min(fromLat, toLat),
    ]);
    const max = fromLonLat([
      Math.max(fromLon, toLon),
      Math.max(fromLat, toLat),
    ]);

    const extent = [min[0], min[1], max[0], max[1]];

    map.getView().fit(extent, {
      size: map.getSize(),
      padding: [50, 50, 50, 50], // Ajustez les marges si nécessaire
    });
  }

  onMount(() => {
    tileLayer = new TileLayer({
      source: new OSM(),
    });

    roadLayer = new VectorLayer({
      source: vectorSource,
    });

    map = new Map({
      target: "map",
      layers: [tileLayer, roadLayer],
      view: new View({
        center: fromLonLat([middleLongitude, middleLatitude]),
        zoom: 10,
      }),
    });

    // Initialiser la carte avec les points et centrer la vue
    fitMapToExtent(fromLongitude, fromLatitude, toLongitude, toLatitude);
  });

  $: {
    if (tileLayer && map) {
      tileLayer.on("prerender", (evt) => {
        if (evt.context) {
          const context = evt.context as CanvasRenderingContext2D;
          if ($mode === "dark") {
            context.filter = "grayscale(80%) invert(100%) ";
            context.globalCompositeOperation = "source-over";
          } else {
            context.filter = "none";
          }
        }
      });

      tileLayer.on("postrender", (evt) => {
        if (evt.context) {
          const context = evt.context as CanvasRenderingContext2D;
          if ($mode === "dark") {
            context.filter = "none";
          }
        }
      });

      tileLayer.changed(); // Force the layer to re-render
    }
  }
</script>

  <div id="map" class="w-full xl:h-full h-60"></div>
